/**
 * Web chat cards for files the agent creates (`generateImage`,
 * `createDocument`, `createPage`, `artifactGet`), drawn the way Gemini draws
 * them.
 *
 * - An image is not a card: it is a block in the response at its own ratio,
 *   never cropped (`GeneratedImage`), with Gemini's loading gradient while it
 *   is made. A failed or refused image is one quiet line with Try again.
 * - A document, deck or page is the canvas entry card: a flat
 *   surface-container tile with the kind glyph, the title (two lines at most),
 *   "Document · <time>" and a filled Open pill. Open shows it in the docked
 *   canvas panel beside the chat; downloads live in the panel, not on the
 *   card.
 * - While the streaming writer builds one (plan 7.2) the same tile shows the
 *   spinner in place of the glyph, "Building the page · 142 lines", and a
 *   96px mini view under it: the newest lines of code for a page, the outline
 *   for a document or deck. Open works from the first event (a page opens on
 *   its code). When the build ends and the result lands, the mini view
 *   collapses (200 ms) back into the Phase 1 card. Without a writer the card
 *   says "Building the page" with elapsed seconds and no mini view.
 *
 * `artifact-card.native.tsx` is the iOS/Android twin with the same exports.
 * `ArtifactPreviewCard` also renders a Task run's files in the run panel.
 */
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type MouseEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { useAui } from "@assistant-ui/react";
import { QUERY_KEYS } from "../../../lib/query-keys";
import { emitPanelCommand } from "../../../lib/panel-commands";
import { MaterialSymbol } from "../../icons/MaterialSymbol";
import { SpinnerGlyph } from "../../common/SpinnerGlyph";
import { ArtifactViewer } from "../../artifacts/ArtifactViewer";
import { GeneratedImage } from "../../artifacts/GeneratedImage";
import {
  openArtifactPanel,
  openArtifactPanelBuilding,
} from "../../artifacts/artifact-panel-store";
import {
  classifyArtifactError,
  formatArtifactTime,
  imageGridLayout,
  isArtifactEditCall,
  mergeArtifactModel,
  parseArtifactToolResult,
  pendingArtifactModel,
  type ArtifactCardModel,
} from "../../artifacts/artifact-model";
import {
  HtmlLineHighlighter,
  buildingCardPhase,
  draftDetailCopy,
  draftFailureKey,
  draftKindForCall,
  draftMotion,
  draftOpenView,
  draftStatusCopy,
  elapsedSeconds,
  outlineWindow,
  type ArtifactDraft,
  type BuildingCardPhase,
} from "../../artifacts/artifact-draft-model";
import {
  artifactCallFirstSeen,
  useArtifactDraft,
} from "../../artifacts/artifact-draft-store";
import { useNow, usePrefersReducedMotion } from "../../artifacts/building-hooks";
import { useArtifactDetail } from "../../../hooks/useArtifact";
import { useArtifactCardSite, usePublishingCapability } from "../../../hooks/useSites";
import { isSiteLive, siteHost } from "../../sites/publish-sheet-model";
import { runIfComposerAvailable } from "../composer-errors";
import { sendArtifactQuickAction } from "../../../lib/artifact-ask";
import { cardVersionNumber } from "../../artifacts/versions/version-model";
import { ShimmerLine } from "./shimmer";
import type { AgentArtifactSummary } from "@tabbio/types";
import type { ToolCardProps } from "./types";

const KIND_ICONS: Record<ArtifactCardModel["kind"], string> = {
  document: "description",
  slides: "slideshow",
  page: "web",
  image: "image",
  file: "draft",
};

/** A creating or editing call on the live stream, as the building card reads it. */
export type CardBuild = {
  toolCallId: string;
  draft: ArtifactDraft | null;
  running: boolean;
  streamable: boolean;
  hasResult: boolean;
  /** An agent edit of an existing file: the card says Editing. */
  mode: "create" | "edit";
};

export const ArtifactCard: FC<ToolCardProps> = ({
  toolCallId,
  toolName,
  result,
  args,
  stage,
}) => {
  const parsed = useMemo(() => parseArtifactToolResult(result), [result]);
  // An agent edit of a page or a document streams like a build (plan 3.3),
  // an image edit renders off the call.
  const editCall = isArtifactEditCall(toolName, args);
  const streamable =
    draftKindForCall(toolName, args) !== null ||
    (editCall && toolName !== "editImage");
  // Only a call the writer can stream subscribes to the draft store; every
  // other card never re-renders for a draft.
  const draft = useArtifactDraft(streamable ? toolCallId : null);
  // Before its result a call draws its pending card from its own arguments
  // (an image takes the requested ratio, a document its title and kind). An
  // image keeps its placeholder until a result replaces it.
  const artifact =
    parsed ??
    (stage === "running" || toolName === "generateImage" || draft
      ? pendingArtifactModel(toolName, args)
      : null);
  if (!artifact) return null;
  const prompt = typeof args?.prompt === "string" ? args.prompt : undefined;
  const build: CardBuild | undefined = streamable
    ? {
        toolCallId,
        draft,
        running: stage === "running",
        streamable,
        hasResult: Boolean(parsed),
        mode: editCall ? "edit" : "create",
      }
    : undefined;
  return (
    <ArtifactPreviewCard artifact={artifact} retryPrompt={prompt} build={build} />
  );
};

export const ArtifactPreviewCard: FC<{
  artifact: ArtifactCardModel;
  retryPrompt?: string;
  /** Chat only: the creating call behind the card, for the building state. */
  build?: CardBuild;
}> = ({ artifact: initial, retryPrompt, build }) => {
  const live = useArtifactDetail(initial.artifactId || null, {
    enabled:
      Boolean(initial.artifactId) &&
      (initial.status === "pending" || !initial.createdAt),
  });
  const artifact = mergeArtifactModel(initial, live.data);
  const gone =
    Boolean(live.error) && classifyArtifactError(live.error) === "not-found";

  if (artifact.kind === "image") {
    if (gone) return <ArtifactNote text="chat.artifacts.unavailable" />;
    if (artifact.status === "failed") {
      return (
        <ArtifactNote
          text={
            artifact.failure?.code === "IMAGE_REFUSED"
              ? "chat.artifacts.refused"
              : "chat.artifacts.failed"
          }
          retryPrompt={retryPrompt}
        />
      );
    }
    return <GeneratedImageGroup models={[artifact]} />;
  }

  return (
    <CanvasCard
      artifact={artifact}
      gone={gone}
      build={build}
      summary={live.data}
      summaryAt={live.dataUpdatedAt}
    />
  );
};

/** One image spans the column; several wrap in Gemini's 490px grid. */
const GeneratedImageGroup: FC<{ models: ArtifactCardModel[] }> = ({
  models,
}) => {
  const layout = imageGridLayout(models.length);
  if (layout.mode === "single") {
    return <GeneratedImage model={models[0]!} />;
  }
  return (
    <div className="tabbio-image-grid">
      {models.map((model, index) => (
        <GeneratedImage
          key={model.artifactId || index}
          model={model}
          tileWidth={layout.itemWidth}
        />
      ))}
    </div>
  );
};

/** A failed or refused image: one line in the response column, no card. */
const ArtifactNote: FC<{ text: string; retryPrompt?: string }> = ({
  text,
  retryPrompt,
}) => {
  const { t } = useTranslation("common");
  return (
    <div className="tabbio-artifact-note" role="status">
      <span dir="auto">{t(text)}</span>
      {retryPrompt ? <RetryButton prompt={retryPrompt} /> : null}
    </div>
  );
};

/** Sends the image request again. Only rendered inside a chat thread. */
const RetryButton: FC<{ prompt: string }> = ({ prompt }) => {
  const { t } = useTranslation("common");
  const aui = useAui();
  return (
    <button
      type="button"
      className="tabbio-text-button"
      onClick={() => {
        runIfComposerAvailable(() => {
          const composer = aui.composer();
          composer.setText(prompt);
          composer.send();
        });
      }}
    >
      {t("chat.artifacts.actions.tryAgain")}
    </button>
  );
};

/**
 * Try again on a failed build: sends the user's message of this turn once
 * more, like an image's Try again. Only rendered inside a chat thread.
 */
const ResendTurnButton: FC = () => {
  const { t } = useTranslation("common");
  const aui = useAui();
  return (
    <button
      type="button"
      className="tabbio-text-button tabbio-canvas-retry"
      onClick={(event) => {
        event.stopPropagation();
        runIfComposerAvailable(() => {
          const message = aui.message().getState();
          const parent = aui
            .thread()
            .getState()
            .messages.find((item) => item.id === message.parentId);
          const text = (parent?.content ?? [])
            .map((part) => (part.type === "text" ? part.text : ""))
            .join("\n")
            .trim();
          if (!text) {
            aui.message().reload();
            return;
          }
          const composer = aui.composer();
          composer.setText(text);
          composer.send();
        });
      }}
    >
      {t("chat.artifacts.actions.tryAgain")}
    </button>
  );
};

/**
 * Keeps the last mini view on screen while it collapses (200 ms, none under
 * reduced motion) after the build ends, so the card settles instead of
 * jumping.
 */
function useSettlingDraft(
  draft: ArtifactDraft | null,
  showing: boolean,
  settleMs: number,
): ArtifactDraft | null {
  const last = useRef<ArtifactDraft | null>(null);
  const [settling, setSettling] = useState<ArtifactDraft | null>(null);
  useEffect(() => {
    if (showing) {
      last.current = draft;
      return;
    }
    const previous = last.current;
    last.current = null;
    if (previous && settleMs > 0) setSettling(previous);
  }, [draft, showing, settleMs]);
  // Its own effect, so a later draft update (the result arriving) cannot
  // cancel the timer and leave the collapsed slot behind.
  useEffect(() => {
    if (!settling) return;
    const timer = setTimeout(() => setSettling(null), settleMs);
    return () => clearTimeout(timer);
  }, [settling, settleMs]);
  return showing ? null : settling;
}

/** Gemini's canvas entry card for a document, a deck or a page. */
const CanvasCard: FC<{
  artifact: ArtifactCardModel;
  gone: boolean;
  build?: CardBuild;
  /** The artifact's summary when one is in hand (its `site` says where it is published). */
  summary?: AgentArtifactSummary;
  summaryAt?: number;
}> = ({ artifact, gone, build, summary, summaryAt }) => {
  const { t, i18n } = useTranslation("common");
  const [overlay, setOverlay] = useState(false);
  const reduceMotion = usePrefersReducedMotion();
  const motion = draftMotion(reduceMotion);
  const draft = build?.draft ?? null;
  const phase: BuildingCardPhase = gone
    ? "settled"
    : build
      ? buildingCardPhase({
          draft,
          result: build.hasResult ? { status: artifact.status } : null,
          running: build.running,
          streamable: build.streamable,
        })
      : artifact.status === "failed"
        ? "failed"
        : "settled";
  const building = phase === "building" && draft !== null;
  const waiting = phase === "waiting";
  // An edit sends no code or outline to watch: its card says Editing alone.
  const showMini = building && draft?.mode !== "edit";
  const settling = useSettlingDraft(draft, showMini, motion.settleMs);
  const since = build ? artifactCallFirstSeen(build.toolCallId) : 0;
  const now = useNow(waiting);
  // Whether this card watched a build, so its end is announced once.
  const [watched, setWatched] = useState(false);
  useEffect(() => {
    if (building || waiting) setWatched(true);
  }, [building, waiting]);

  const kindLabel = t(`chat.artifacts.kind.${artifact.kind}`);
  const title = draft?.title || artifact.title || kindLabel;
  const pending = artifact.status === "pending" && !gone;
  const failureCode = draft?.error?.code ?? artifact.failure?.code ?? null;
  const canOpen =
    artifact.status === "ready" && !gone && Boolean(artifact.artifactId);
  const canOpenBuilding = building && Boolean(draft?.artifactId);
  const time = formatArtifactTime(artifact.createdAt, i18n.language || "en");
  // A published file says where (plan 7.6): "Published at <host>", from the
  // summary's `site` (the site route only when the summary has none).
  const publishing = usePublishingCapability();
  const site = useArtifactCardSite(artifact.artifactId, summary, {
    summaryAt,
    enabled:
      Boolean(publishing?.available) &&
      canOpen &&
      phase === "settled" &&
      artifact.kind !== "file",
  });
  const liveHost = isSiteLive(site) ? siteHost(site?.url) : "";

  let secondary: string;
  if (gone) secondary = t("chat.artifacts.unavailable");
  else if (phase === "failed") secondary = t(draftFailureKey(failureCode));
  else if (building && draft) {
    const status = t(draftStatusCopy(draft).key);
    const detail = draftDetailCopy(draft);
    secondary = detail
      ? t("chat.artifacts.building.meta", {
          status,
          detail: t(detail.key, detail.params),
        })
      : status;
  } else if (waiting) {
    secondary = t("chat.artifacts.building.meta", {
      status: t(
        build?.mode === "edit"
          ? "chat.artifacts.building.editing"
          : `chat.artifacts.building.${
              artifact.kind === "page" || artifact.kind === "slides"
                ? artifact.kind
                : "document"
            }`,
      ),
      detail: t("chat.artifacts.building.elapsed", {
        count: elapsedSeconds(since, now),
      }),
    });
  } else if (pending) secondary = t("chat.artifacts.preparing");
  else if (artifact.saved?.edited) {
    // An agent edit's card names the version it saved.
    secondary = t("chat.artifacts.card.versionSaved", {
      number: artifact.saved.versionNumber,
    });
    if (liveHost) {
      secondary = `${secondary} · ${t("chat.artifacts.publish.publishedAt", { host: liveHost })}`;
    }
  } else {
    secondary = time
      ? t("chat.artifacts.createdAt", { kind: kindLabel, time })
      : kindLabel;
    // Past the first version the card says which one opens (plan 7.5).
    const versionNumber = cardVersionNumber(artifact.versionNumber);
    if (versionNumber) {
      secondary = `${secondary} · ${t("chat.artifacts.versions.cardMeta", { number: versionNumber })}`;
    }
    if (liveHost) {
      secondary = `${secondary} · ${t("chat.artifacts.publish.publishedAt", { host: liveHost })}`;
    }
  }

  // The live region speaks at the start, the end and a failure only, never
  // for each delta (plan 7.8); the meta line itself is not a live region.
  const buildingKind =
    artifact.kind === "page" || artifact.kind === "slides"
      ? artifact.kind
      : "document";
  const announcement = !build
    ? ""
    : phase === "failed"
      ? secondary
      : building && draft
        ? t(draftStatusCopy({ ...draft, phase: "start" }).key)
        : waiting
          ? t(
              build.mode === "edit"
                ? "chat.artifacts.building.editing"
                : `chat.artifacts.building.${buildingKind}`,
            )
          : watched
            ? t("chat.artifacts.building.finished", { title })
            : "";

  const open = (event: MouseEvent<HTMLElement>) => {
    if (canOpenBuilding && draft) {
      openArtifactPanelBuilding(
        draft.artifactId,
        draftOpenView(draft.kind, draft.mode),
      );
      return;
    }
    if (!canOpen) return;
    // Open shows the head: read it again, so a card of an edit never opens
    // the version the panel had before (user test fix).
    emitPanelCommand({
      type: "data.invalidate",
      keys: [QUERY_KEYS.ARTIFACT(artifact.artifactId)],
    });
    // Inside a dialog (the Tasks run panel) the docked panel would sit behind
    // it, so the canvas covers the viewport instead.
    if (event.currentTarget.closest('[role="dialog"]')) {
      setOverlay(true);
      return;
    }
    openArtifactPanel(artifact.artifactId);
  };
  const openable = canOpen || canOpenBuilding;
  const miniDraft = showMini ? draft : settling;
  // A saved version the checks still flag: a success, with what is left.
  const blocking =
    phase === "settled" && !gone && artifact.artifactId
      ? (artifact.saved?.blocking ?? 0)
      : 0;
  const spinning = building || waiting;

  return (
    <>
      <div
        className={`tabbio-canvas-card${openable ? " is-openable" : ""}${miniDraft ? " has-mini" : ""}`}
        onClick={openable ? open : undefined}
      >
        {spinning ? (
          <span
            className="tabbio-canvas-card-glyph tabbio-canvas-card-spinner"
            aria-hidden
          >
            <SpinnerGlyph color="var(--text-secondary)" size={20} />
          </span>
        ) : (
          <MaterialSymbol
            name={KIND_ICONS[artifact.kind]}
            size={20}
            className="tabbio-canvas-card-glyph"
          />
        )}
        <div className="tabbio-canvas-card-body">
          {/* A pending card shows the title its call named; a shimmer
              only while there is none yet. */}
          {pending && !spinning && !artifact.title ? (
            <div className="tabbio-canvas-card-shimmer" aria-hidden>
              <ShimmerLine width="70%" height={16} />
            </div>
          ) : (
            <div className="tabbio-canvas-card-title" dir="auto">
              {title}
            </div>
          )}
          <div
            className="tabbio-canvas-card-meta"
            dir="auto"
            role={pending && !spinning && !build ? "status" : undefined}
          >
            {secondary}
          </div>
          {blocking > 0 ? (
            <div className="tabbio-canvas-card-findings" dir="auto">
              <span>
                {t("chat.artifacts.card.fixBefore", { count: blocking })}
              </span>
              <button
                type="button"
                className="tabbio-text-button"
                onClick={(event) => {
                  event.stopPropagation();
                  sendArtifactQuickAction({
                    text: t("chat.artifacts.edit.fixPrompt"),
                    context: { artifactId: artifact.artifactId },
                    threadId: null,
                  });
                }}
              >
                {t("chat.artifacts.edit.fixWithTabbio")}
              </button>
            </div>
          ) : null}
        </div>
        {openable ? (
          <button
            type="button"
            className="tabbio-canvas-open"
            onClick={(event) => {
              event.stopPropagation();
              open(event);
            }}
            aria-label={`${t("chat.artifacts.actions.open")}: ${title}`}
          >
            {t("chat.artifacts.actions.open")}
          </button>
        ) : phase === "failed" && build && !gone ? (
          <ResendTurnButton />
        ) : null}
        {miniDraft ? (
          <div
            className={`tabbio-canvas-mini-slot${showMini ? "" : " is-collapsing"}`}
            aria-hidden
          >
            <div className="tabbio-canvas-mini-clip">
              <DraftMiniView
                draft={miniDraft}
                caretBlinks={motion.caretBlinks}
              />
            </div>
          </div>
        ) : null}
        {build ? (
          <span className="tabbio-visually-hidden" aria-live="polite">
            {announcement}
          </span>
        ) : null}
      </div>
      {/* Outside the card: clicks in the overlay must not bubble into it. */}
      {overlay ? (
        <ArtifactViewer
          artifactId={artifact.artifactId}
          kindHint={artifact.kind}
          onClose={() => setOverlay(false)}
        />
      ) : null}
    </>
  );
};

/** The 96px well under a building card: code for a page, the outline otherwise. */
const DraftMiniView: FC<{ draft: ArtifactDraft; caretBlinks: boolean }> = ({
  draft,
  caretBlinks,
}) =>
  draft.kind === "page" ? (
    <CodeMini code={draft.code} caretBlinks={caretBlinks} />
  ) : (
    <OutlineMini draft={draft} />
  );

/**
 * The last five lines of the page's code, monospace, tag names in the
 * primary text colour, a fade at the top and a block caret on the newest
 * line. Always left to right, like code.
 */
const CodeMini: FC<{ code: string; caretBlinks: boolean }> = ({
  code,
  caretBlinks,
}) => {
  const highlighter = useRef<HtmlLineHighlighter | null>(null);
  highlighter.current ??= new HtmlLineHighlighter();
  highlighter.current.update(code);
  const lines = highlighter.current.lastLines(5);
  if (lines.length === 0) lines.push([]);
  return (
    <div className="tabbio-draft-mini is-code" dir="ltr">
      <div className="tabbio-draft-mini-lines">
        {lines.map((tokens, index) => (
          <div key={index} className="tabbio-draft-mini-line">
            {tokens.map((token, tokenIndex) => (
              <span
                key={tokenIndex}
                className={token.kind === "tag" ? "is-tag" : undefined}
              >
                {token.text}
              </span>
            ))}
            {index === lines.length - 1 ? (
              <span
                className={`tabbio-draft-caret${caretBlinks ? " is-blinking" : ""}`}
              />
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

/** Up to four headings, each done (check) or being written (spinner), then "+2 more". */
const OutlineMini: FC<{ draft: ArtifactDraft }> = ({ draft }) => {
  const { t } = useTranslation("common");
  const { rows, more } = outlineWindow(draft.outline);
  return (
    <div className="tabbio-draft-mini is-outline">
      {rows.map((row, index) => (
        <div key={index} className="tabbio-draft-outline-row">
          <span className="tabbio-draft-outline-mark">
            {row.done ? (
              <MaterialSymbol name="check" size={16} />
            ) : row.current ? (
              <SpinnerGlyph color="var(--text-secondary)" size={14} />
            ) : (
              <MaterialSymbol name="radio_button_unchecked" size={14} />
            )}
          </span>
          <span className="tabbio-draft-outline-heading" dir="auto">
            {row.heading}
          </span>
        </div>
      ))}
      {more > 0 ? (
        <div className="tabbio-draft-outline-more" dir="auto">
          {t("chat.artifacts.building.more", { count: more })}
        </div>
      ) : null}
    </div>
  );
};
